/**
 * MasterClientDetail — Fiche Master CRM "Identité pure" (PSI Phase 8 + Ajustement 1).
 *
 * Version épurée :
 *   1. Header dossier maître (couple_display_name) — modifiable + bouton "Archiver"
 *   2. Membres (Principal + Secondaire) — CRUD complet (modif + suppression)
 *   3. Coordonnées communes (adresse couple, tel maison, mode contact)
 *   4. Historique Stripe (lecture seule)
 *
 * Activation fiscal/immeuble et outils Google : module Bureau. Accès aux tuiles portail :
 * Cabinet → Portail clients → onglet « Accès aux tuiles ». Sous-composants dans ./masterClientDetail/.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, ArrowLeft, Receipt, Trash2,
} from "lucide-react";
import { toast } from "@/lib/toast";
import { useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { MasterHeader, CommonContactSection, BillingSection, UserCard, EditUserModal, ConfirmArchiveModal, Stat } from "./masterClientDetail/sections";
import { ExpertiseProfilesSection } from "./masterClientDetail/expertise";
import { ComplianceSection } from "./masterClientDetail/ComplianceSection";
import { API, authH } from "@/api";


export default function MasterClientDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [master, setMaster] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [invoices, setInvoices] = useState(null);
  const [showArchive, setShowArchive] = useState(false);
  const [editingUser, setEditingUser] = useState(null); // 'primary' | 'secondary'

  // Ajustement 3 — Protection contre la perte de saisie pendant la navigation.
  // L'état `dirty` est activé dès qu'un sous-formulaire (rename, common contact, edit user)
  // entre en mode édition. Il est désactivé après save/cancel.
  const [dirty, setDirty] = useState(false);
  const confirmLeave = useUnsavedChanges(dirty);

  // Batch #14.122 — Navigation contextuelle vers le bon Hub (sidebar 9 entrées).
  // Depuis Fiscal → /admin/fiscal-dossiers ; depuis Immeubles → /admin/immeuble-dossiers ;
  // sinon (Master) → /admin/masters.
  const backInfo = location.pathname.startsWith("/admin/base-fiscale")
    ? { path: "/admin/fiscal-dossiers", label: "Retour aux Dossiers Fiscaux", origin: "Expertise Fiscale" }
    : location.pathname.startsWith("/admin/base-immeuble")
    ? { path: "/admin/immeuble-dossiers", label: "Retour aux Dossiers Immeubles", origin: "Expertise Immeubles" }
    : { path: "/admin/masters", label: "Retour à Master", origin: "Master" };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/masters/${id}`, { headers: authH() });
      setMaster(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadInvoices = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/masters/${id}/invoices`, { headers: authH() });
      setInvoices(r.data);
    } catch {
      setInvoices(null);
    }
  }, [id]);

  useEffect(() => { load(); loadInvoices(); }, [load, loadInvoices]);

  // Batch #14.122 — Breadcrumb contextuel : Accueil › <Hub> › <Liste> › <couple>
  const { setTrail } = useBreadcrumb();
  useEffect(() => {
    if (!master) return undefined;
    const couple = master.couple_display_name || "(Master sans nom)";
    // Détermine le Hub d'origine selon la route (Fiscal / Immeubles / Cabinet)
    const trail = location.pathname.startsWith("/admin/base-fiscale")
      ? [
          { label: "Fiscal", href: "/admin/fiscal" },
          { label: "Dossiers Fiscaux", href: "/admin/fiscal-dossiers" },
          { label: couple },
        ]
      : location.pathname.startsWith("/admin/base-immeuble")
      ? [
          { label: "Immeubles", href: "/admin/immeubles" },
          { label: "Dossiers Immeubles", href: "/admin/immeuble-dossiers" },
          { label: couple },
        ]
      : [
          { label: "Cabinet", href: "/admin/cabinet" },
          { label: "Master — Registre", href: "/admin/masters" },
          { label: couple },
        ];
    setTrail(trail);
    return () => setTrail(null);
  }, [master, location.pathname, setTrail]);

  const unlinkSecondary = async () => {
    if (!window.confirm("Délier le conjoint/secondaire du dossier maître ? Le compte client reste actif.")) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/masters/${id}/unlink-secondary`, {}, { headers: authH() });
      setMaster(r.data);
      toast.success("Secondaire délié");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const saveCommonContact = async (cc) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/masters/${id}`, { common_contact: cc }, { headers: authH() });
      setMaster(r.data);
      toast.success("Coordonnées communes enregistrées");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const saveBilling = async (billing) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/masters/${id}`, { billing }, { headers: authH() });
      setMaster(r.data);
      toast.success(billing.name?.trim() ? "Nom facturé enregistré" : "Nom facturé retiré");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      return false;
    } finally { setBusy(false); }
  };

  const updateMasterName = async (newName, clientNumber) => {
    setBusy(true);
    try {
      const payload = { couple_display_name: newName };
      if (clientNumber !== undefined && clientNumber !== (master?.client_number || "")) payload.client_number = clientNumber;
      const r = await axios.put(`${API}/masters/${id}`, payload, { headers: authH() });
      setMaster(r.data);
      toast.success(payload.client_number !== undefined ? "Dossier et n° de client mis à jour" : "Nom du dossier mis à jour");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
      return false;
    } finally { setBusy(false); }
  };

  const updateUserFields = async (clientId, fields) => {
    setBusy(true);
    try {
      await axios.put(`${API}/clients/${clientId}`, fields, { headers: authH() });
      await load();
      toast.success("Informations du membre mises à jour");
      setEditingUser(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const deleteUser = async (clientId, who) => {
    if (!window.confirm(
      `Supprimer définitivement le ${who} ? Le compte sera envoyé à la Corbeille (récupérable 30 jours).`
    )) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/clients/${clientId}`, { headers: authH() });
      toast.success(`${who} supprimé (envoyé à la corbeille)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  const archiveMaster = async () => {
    setBusy(true);
    try {
      await axios.delete(`${API}/masters/${id}`, { headers: authH() });
      toast.success("Dossier Master archivé");
      navigate("/admin/masters");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  if (loading) return <div className="text-center py-20"><Loader2 className="inline w-7 h-7 animate-spin text-slate-400" /></div>;
  if (!master) return null;

  return (
    <div className="space-y-5" data-testid="admin-master-detail">
      <div className="flex items-center justify-between">
        <Link
          to={backInfo.path}
          onClick={(e) => { if (!confirmLeave()) e.preventDefault(); }}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          data-testid="masters-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> {backInfo.label}
        </Link>
        <button
          onClick={() => setShowArchive(true)}
          disabled={busy}
          className="text-xs px-2.5 py-1 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 disabled:opacity-50 inline-flex items-center gap-1"
          data-testid="masters-archive-btn"
        >
          <Trash2 className="w-3 h-3" /> Archiver le dossier
        </button>
      </div>

      {/* Header dossier maître */}
      <MasterHeader master={master} onSaveName={updateMasterName} busy={busy} onDirty={setDirty} />

      {/* Coordonnées communes */}
      <CommonContactSection master={master} onSave={saveCommonContact} busy={busy} onDirty={setDirty} />

      {/* Batch #14.310 — Facturation (nom facturé) */}
      <BillingSection master={master} onSave={saveBilling} busy={busy} onDirty={setDirty} />

      {/* Membres CRUD */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <UserCard
          user={master.primary_user}
          role="Principal"
          badge="bg-indigo-100 text-indigo-700"
          onEdit={() => setEditingUser("primary")}
          onDelete={() => deleteUser(master.primary_user.id, "client principal")}
          busy={busy}
        />
        {master.secondary_user ? (
          <UserCard
            user={master.secondary_user}
            role="Secondaire"
            badge="bg-purple-100 text-purple-700"
            onEdit={() => setEditingUser("secondary")}
            onDelete={() => deleteUser(master.secondary_user.id, "client secondaire")}
            onUnlink={unlinkSecondary}
            busy={busy}
          />
        ) : (
          <div className="border border-dashed border-slate-300 rounded-lg p-4 text-center text-sm text-slate-500">
            Aucun conjoint·e lié
          </div>
        )}
      </div>

      {/* Stats Stripe (lecture seule) */}
      <div className="border border-slate-200 rounded-xl p-4" data-testid="masters-stripe-section">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-slate-800 inline-flex items-center gap-2">
            <Receipt className="w-5 h-5 text-slate-500" /> Historique Stripe
          </h2>
          <span className="text-[10px] text-slate-400">Agrégation lecture seule</span>
        </div>
        {!invoices ? (
          <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
        ) : invoices.nb_invoices === 0 ? (
          <div className="text-sm text-slate-400 italic">Aucune facture liée aux comptes de ce dossier.</div>
        ) : (
          <div className="grid grid-cols-3 gap-3 text-center">
            <Stat label="Factures" value={invoices.nb_invoices} />
            <Stat label="Payées" value={invoices.nb_paid} tone="emerald" />
            <Stat label="Total (CAD)" value={`${invoices.total_amount_cad.toFixed(2)} $`} tone="indigo" />
          </div>
        )}
      </div>

      {/* Batch #14.326 — Conformité : prochaines échéances */}
      <ComplianceSection masterId={master.id} />

      {/* Ajustement 2 — Fiches expertises (lecture seule ici) */}
      <ExpertiseProfilesSection master={master} />

      {/* Modale édition membre */}
      {editingUser && (
        <EditUserModal
          user={editingUser === "primary" ? master.primary_user : master.secondary_user}
          role={editingUser === "primary" ? "Principal" : "Secondaire"}
          onClose={() => setEditingUser(null)}
          onSave={(fields) => updateUserFields(
            editingUser === "primary" ? master.primary_user.id : master.secondary_user.id,
            fields,
          )}
          busy={busy}
        />
      )}

      {/* Modale archivage Master */}
      {showArchive && (
        <ConfirmArchiveModal
          masterName={master.couple_display_name}
          onClose={() => setShowArchive(false)}
          onConfirm={() => { setShowArchive(false); archiveMaster(); }}
          busy={busy}
        />
      )}
    </div>
  );
}

